import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  AppAdminFormLabel,
  AppCard,
  AppCheckbox,
  AppSelect,
  AppTable,
  AppTextField,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { convGtJDate } from '@/utils/dateConverter';
import { partsToTime, timeToParts } from '@/utils/timeParts';
import { convertSeirekiWrap } from '@/utils/wareki';
import { WEEKDAY_LABELS_WITH_KEY } from '@/constants/weekday';
import type { CreateClinicLocationManagerFormData } from './create-manager-schema';

const AM_HOUR_OPTIONS = [
  { value: '', label: '---' },
  ...Array.from({ length: 12 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') })),
];
const PM_HOUR_OPTIONS = [
  { value: '', label: '---' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 12,
    label: String(i + 12).padStart(2, '0'),
  })),
];
/** 拠点施術管理者の編集画面のみ: 午前・午後とも 00–23 時を選択可能 */
const HOUR_OPTIONS_00_23 = [
  { value: '', label: '---' },
  ...Array.from({ length: 24 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') })),
];
const MINUTE_OPTIONS = [
  { value: '', label: '---' },
  ...Array.from({ length: 60 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') })),
];

/** Matches CreateClinicLocationManagerPage defaultServiceBlock wide_area_union_rows shape. */
const EMPTY_WIDE_AREA_ROW = {
  wide_area_union_code: undefined as string | undefined,
  wide_area_union_number: undefined as string | undefined,
  prefecture_number: undefined as string | undefined,
};
const DEFAULT_WIDE_AREA_ROWS = [{ ...EMPTY_WIDE_AREA_ROW }];
/** Max rows rendered in 保険者別番号 table (sort/filter apply to full data; only the first N rows show). */
const WIDE_AREA_TABLE_MAX_ROWS = 30;

type InsurerSortField = 'wide_area_union_code' | 'wide_area_union_number' | 'prefecture_number';
type SortDir = 'ascend' | 'descend';

type ServiceBlockFieldsProps = {
  boxPrefix: 'a2' | 'r2';
  boxLabel: string;
  form: UseFormReturn<CreateClinicLocationManagerFormData>;
  wideAreaLoading?: boolean;
  /** Disabled when therapist does not have cert for this service */
  disabled?: boolean;
  /** When this or disabled changes, useFieldArray.replace syncs 保険者別番号 rows (setValue alone can leave stale UI). */
  massagerId?: number | null;
  /** Parent bumps after setValue on wide_area_union_rows so replace() runs when array length is unchanged. */
  wideAreaSyncToken?: number;
  /** Show 必須 on 適用期間 when this is the only enabled service block (schema: at least one FROM). */
  requirePeriod?: boolean;
  /** When true: 開始/終了 allow 00–23 for both 午前 and 午後. When false: 午前 00–11 / 午後 12–23. */
  fullDayHourSelect?: boolean;
};

export function ServiceBlockFields({
  boxPrefix,
  boxLabel,
  form,
  wideAreaLoading = false,
  disabled = false,
  massagerId = null,
  wideAreaSyncToken = 0,
  requirePeriod = false,
  fullDayHourSelect = false,
}: ServiceBlockFieldsProps) {
  const prefix = boxPrefix;
  const control = form.control;
  const amHourOptions = fullDayHourSelect ? HOUR_OPTIONS_00_23 : AM_HOUR_OPTIONS;
  const pmHourOptions = fullDayHourSelect ? HOUR_OPTIONS_00_23 : PM_HOUR_OPTIONS;

  const [prefFilter, setPrefFilter] = useState('');
  const [insurerSortField, setInsurerSortField] = useState<InsurerSortField | null>(
    'wide_area_union_code'
  );
  const [insurerSortDir, setInsurerSortDir] = useState<SortDir>('ascend');
  const [displayOrder, setDisplayOrder] = useState<number[]>([]);

  const { fields: wideAreaFields, replace: replaceWideAreaRows } = useFieldArray({
    control,
    name: `${prefix}.wide_area_union_rows` as 'a2.wide_area_union_rows',
  });

  useEffect(() => {
    if (disabled) setPrefFilter('');
  }, [disabled, massagerId]);

  const wideAreaRows = form.watch(`${prefix}.wide_area_union_rows`) ?? [];

  /**
   * Disabled: clear 保険者別番号 in layout (parent useLayoutEffect may run after child — children run first).
   * Else: sync useFieldArray length when form array length differs.
   */
  useLayoutEffect(() => {
    if (disabled) {
      const cleared = [{ ...EMPTY_WIDE_AREA_ROW }];
      form.setValue(`${prefix}.wide_area_union_rows`, cleared, { shouldValidate: false });
      replaceWideAreaRows(cleared);
      return;
    }
    const rows = form.getValues(`${prefix}.wide_area_union_rows` as const);
    const list = Array.isArray(rows) ? rows : [];
    if (wideAreaFields.length === list.length) return;
    replaceWideAreaRows(list.length > 0 ? list : DEFAULT_WIDE_AREA_ROWS);
  }, [
    disabled,
    wideAreaFields.length,
    wideAreaRows.length,
    wideAreaSyncToken,
    form,
    prefix,
    replaceWideAreaRows,
  ]);

  /**
   * Same-length array after parent setValue (cert clear / pref): useFieldArray keeps stale row ids; replace from getValues.
   */
  useLayoutEffect(() => {
    if (disabled) return;
    const rows = form.getValues(`${prefix}.wide_area_union_rows` as const);
    const list = Array.isArray(rows) ? rows : [];
    replaceWideAreaRows(list.length > 0 ? list : DEFAULT_WIDE_AREA_ROWS);
  }, [wideAreaSyncToken, disabled, form, prefix, replaceWideAreaRows]);

  const onWarekiDateChange =
    (fieldOnChange: (v: string | null) => void, emptyValue: '' | null) => (warekiStr: string) => {
      if (!warekiStr?.trim()) {
        fieldOnChange(emptyValue);
        return;
      }
      const seireki = convertSeirekiWrap(warekiStr);
      const d = dayjs(seireki.replace(/\//g, '-'));
      fieldOnChange(d.isValid() ? d.format('YYYY-MM-DD') : emptyValue);
    };

  useEffect(() => {
    const n = wideAreaRows.length;
    setDisplayOrder(prev => (prev.length !== n ? Array.from({ length: n }, (_, i) => i) : prev));
  }, [wideAreaRows.length]);

  useEffect(() => {
    if (wideAreaRows.length === 0) return;
    const sortField = insurerSortField ?? 'wide_area_union_code';
    const indices = Array.from({ length: wideAreaRows.length }, (_, i) => i);
    const sorted = [...indices].sort((a, b) => {
      const va = (wideAreaRows[a]?.[sortField] ?? '').toString().trim();
      const vb = (wideAreaRows[b]?.[sortField] ?? '').toString().trim();
      const cmp = va.localeCompare(vb, 'ja');
      return insurerSortDir === 'ascend' ? cmp : -cmp;
    });
    setDisplayOrder(sorted);
  }, [insurerSortField, insurerSortDir, wideAreaRows.length, wideAreaRows]);

  const rowHasData = useCallback(
    (
      row:
        | {
            wide_area_union_code?: string | null;
            wide_area_union_number?: string | null;
            prefecture_number?: string | null;
          }
        | undefined
    ) => {
      if (!row) return false;
      const c = (row.wide_area_union_code ?? '').toString().trim();
      const n = (row.wide_area_union_number ?? '').toString().trim();
      const p = (row.prefecture_number ?? '').toString().trim();
      return c !== '' || n !== '' || p !== '';
    },
    []
  );

  const indicesWithData = useMemo(
    () =>
      wideAreaRows
        .map((_: unknown, i: number) => i)
        .filter((i: number) => rowHasData(wideAreaRows[i])),
    [wideAreaRows, rowHasData]
  );

  const { indicesToShow, wideAreaUncappedRowCount, isWideAreaTableCapped } = useMemo(() => {
    const filterTrim = prefFilter.trim();
    let base = displayOrder.filter(i => indicesWithData.includes(i));
    if (filterTrim) {
      base = base.filter(i =>
        (wideAreaRows[i]?.prefecture_number ?? '').toString().trim().includes(filterTrim)
      );
      if (base.length === 0) {
        return {
          indicesToShow: [] as number[],
          wideAreaUncappedRowCount: 0,
          isWideAreaTableCapped: false,
        };
      }
    }
    const emptyIndices = wideAreaRows
      .map((_: unknown, i: number) => i)
      .filter((i: number) => !rowHasData(wideAreaRows[i]));
    // Always surface template empty row(s) in the table when they exist in the form.
    // Do not require wideAreaRows.length < max: with 29 filled + 1 empty the length is 30 and the empty row must still show.
    if (emptyIndices.length > 0) {
      const emptyToShow = base.length === 0 ? 2 : 1;
      const emptyPart = emptyIndices.slice(0, emptyToShow);
      base = [...emptyPart, ...base];
    }
    const wideAreaUncappedRowCount = base.length;
    const isWideAreaTableCapped = wideAreaUncappedRowCount > WIDE_AREA_TABLE_MAX_ROWS;
    const indicesToShow = isWideAreaTableCapped ? base.slice(0, WIDE_AREA_TABLE_MAX_ROWS) : base;
    return { indicesToShow, wideAreaUncappedRowCount, isWideAreaTableCapped };
  }, [displayOrder, prefFilter, wideAreaRows, indicesWithData, rowHasData]);

  const boxErrors = form.formState.errors[prefix] as
    | Record<string, { message?: string }>
    | undefined;
  const displayedWithDataCount = useMemo(
    () => indicesToShow.filter(i => indicesWithData.includes(i)).length,
    [indicesToShow, indicesWithData]
  );
  const wideAreaInfoText = useMemo(() => {
    const total = indicesWithData.length;
    const n = displayedWithDataCount;
    let text: string;
    if (total === 0) text = '0 件中 0件から0件 を表示しています';
    else if (n === 0) text = `${total} 件中 0件から0件 を表示しています`;
    else text = `${total} 件中 1件から${n}件 を表示しています`;
    if (isWideAreaTableCapped) {
      text += `（${wideAreaUncappedRowCount}件中${WIDE_AREA_TABLE_MAX_ROWS}件まで表示）`;
    }
    return text;
  }, [
    indicesWithData.length,
    displayedWithDataCount,
    isWideAreaTableCapped,
    wideAreaUncappedRowCount,
  ]);

  const wideAreaDataSource = useMemo(
    () =>
      indicesToShow.map(i => ({
        ...wideAreaRows[i],
        _idx: i,
        // Avoid stale useFieldArray row ids after parent setValue shrinks the array; remount rows when massager changes.
        key: `${prefix}-wa-${i}-m${massagerId ?? 0}`,
      })),
    [indicesToShow, wideAreaRows, massagerId, prefix]
  );

  const wideAreaColumns = useMemo(
    () => [
      {
        title: '保険者番号',
        dataIndex: 'wide_area_union_code',
        key: 'wide_area_union_code',
        sorter: !disabled,
        render: (_: unknown, record: { _idx: number }) => (
          <Controller
            name={`${prefix}.wide_area_union_rows.${record._idx}.wide_area_union_code` as const}
            control={control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-0.5">
                <AppTextField
                  {...field}
                  value={field.value ?? ''}
                  className="w-full !h-[39px]"
                  status={fieldState.error ? 'error' : undefined}
                  disabled={disabled}
                />
                {fieldState.error?.message && (
                  <p className="text-xs text-red-600">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
        ),
      },
      {
        title: '施術機関コード',
        dataIndex: 'wide_area_union_number',
        key: 'wide_area_union_number',
        sorter: !disabled,
        render: (_: unknown, record: { _idx: number }) => (
          <Controller
            name={`${prefix}.wide_area_union_rows.${record._idx}.wide_area_union_number` as const}
            control={control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-0.5">
                <AppTextField
                  {...field}
                  value={field.value ?? ''}
                  className="w-full !h-[39px]"
                  status={fieldState.error ? 'error' : undefined}
                  disabled={disabled}
                />
                {fieldState.error?.message && (
                  <p className="text-xs text-red-600">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
        ),
      },
      {
        title: '県番号',
        dataIndex: 'prefecture_number',
        key: 'prefecture_number',
        sorter: !disabled,
        render: (_: unknown, record: { _idx: number }) => (
          <Controller
            name={`${prefix}.wide_area_union_rows.${record._idx}.prefecture_number` as const}
            control={control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-0.5">
                <AppTextField
                  {...field}
                  value={field.value ?? ''}
                  className="w-full !h-[39px]"
                  status={fieldState.error ? 'error' : undefined}
                  disabled={disabled}
                />
                {fieldState.error?.message && (
                  <p className="text-xs text-red-600">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
        ),
      },
    ],
    [control, prefix, disabled]
  );

  // No pointer-events-none: it blocks hover so cursor-not-allowed never shows on disabled controls.
  const disabledClass = disabled
    ? 'cursor-not-allowed select-none opacity-75 [&_input]:!bg-[#EBEBEB] [&_.ant-input]:!bg-[#EBEBEB] [&_.ant-select-selector]:!bg-[#EBEBEB] [&_.ant-picker]:!bg-[#EBEBEB] [&_input:disabled]:cursor-not-allowed [&_.ant-checkbox-disabled]:cursor-not-allowed [&_.ant-input-disabled]:cursor-not-allowed [&_.ant-select-disabled_.ant-select-selector]:cursor-not-allowed'
    : '';

  return (
    <AppCard title={boxLabel} className={`h-full w-full ${disabledClass}`}>
      {/* 1. 非表示 */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-700">非表示</span>
        <Controller
          name={`${prefix}.is_none_disp` as const}
          control={control}
          render={({ field }) => (
            <AppCheckbox
              checked={!!field.value}
              onChange={e => field.onChange(e.target.checked)}
              disabled={disabled}
            />
          )}
        />
        {wideAreaLoading && (
          <span className="flex items-center gap-1.5 text-sm text-gray-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
            保険者別番号を読み込み中...
          </span>
        )}
      </div>

      {/* 2. 適用期間 */}
      <div className="mb-4 grid grid-cols-1 items-start gap-x-4 gap-y-1 sm:grid-cols-[120px_minmax(0,1fr)]">
        <div className="pt-2 sm:pt-2.5">
          <AppAdminFormLabel
            label="適用期間"
            required={!disabled && requirePeriod}
            className="mb-0 whitespace-nowrap"
          />
        </div>
        <div className="min-w-0 flex flex-col">
          {/* items-start: エリア表示時も「から」前後の入力の縦位置が崩れない */}
          <div className="flex min-w-0 items-start gap-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <Controller
                name={`${prefix}.available_from` as const}
                control={control}
                render={({ field }) => (
                  <RA2NDatePicker
                    value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                    onChange={onWarekiDateChange(field.onChange, '')}
                    placeholder=""
                    error={!disabled && !!boxErrors?.available_from}
                    className="min-w-0 w-full"
                    disabled={disabled}
                  />
                )}
              />
              <div
                className="mt-1 flex min-h-[2.75rem] flex-col justify-start text-sm leading-tight text-red-600"
                aria-live="polite"
              >
                {!disabled && boxErrors?.available_from?.message && (
                  <p className="m-0">{boxErrors.available_from.message}</p>
                )}
              </div>
            </div>
            <span className="shrink-0 pt-2 text-sm leading-none text-gray-600">から</span>
            <div className="flex min-w-0 flex-1 flex-col">
              <Controller
                name={`${prefix}.available_to` as const}
                control={control}
                render={({ field }) => (
                  <RA2NDatePicker
                    value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                    onChange={onWarekiDateChange(field.onChange, null)}
                    placeholder=""
                    error={!disabled && !!boxErrors?.available_to}
                    className="min-w-0 w-full"
                    disabled={disabled}
                  />
                )}
              />
              <div
                className="mt-1 flex min-h-[2.75rem] flex-col justify-start text-sm leading-tight text-red-600"
                aria-live="polite"
              >
                {!disabled && boxErrors?.available_to?.message && (
                  <p className="m-0">{boxErrors.available_to.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. 登録記号番号 */}
      <div className="mb-4 grid grid-cols-1 items-start gap-x-4 gap-y-1 sm:grid-cols-[100px_minmax(0,1fr)]">
        <label className="pt-2 text-sm font-medium text-gray-700 sm:pt-2.5">登録記号番号</label>
        <div className="min-w-0 flex flex-col flex-1">
          <Controller
            name={`${prefix}.location_manager_number` as const}
            control={control}
            render={({ field }) => (
              <AppTextField
                {...field}
                value={field.value ?? ''}
                onChange={e => field.onChange(e.target.value || null)}
                status={!disabled && boxErrors?.location_manager_number ? 'error' : undefined}
                disabled={disabled}
              />
            )}
          />
          {!disabled && boxErrors?.location_manager_number?.message && (
            <p className="mt-1 min-h-[1.25rem] text-sm text-red-600">
              {boxErrors.location_manager_number.message}
            </p>
          )}
        </div>
      </div>

      {/* 4. 保険者別番号 */}
      <div className="mb-4">
        <p className="mb-2 text-sm font-medium text-gray-700">保険者別番号</p>
        <div className="mb-2 flex items-center justify-end gap-2">
          <label
            htmlFor={`prefecture-filter-${prefix}`}
            className="whitespace-nowrap text-sm font-medium text-gray-700"
          >
            県番号で絞り込み
          </label>
          <AppTextField
            id={`prefecture-filter-${prefix}`}
            value={prefFilter}
            onChange={e => setPrefFilter(e.target.value)}
            allowClear
            className="w-28 !h-[39px]"
            disabled={disabled}
          />
        </div>
        <div className="overflow-hidden rounded border border-gray-200">
          <AppTable
            columns={wideAreaColumns}
            dataSource={wideAreaDataSource}
            pagination={false}
            rowKey="key"
            onChange={(_pagination, _filters, sorter) => {
              if (disabled) return;
              const sort = Array.isArray(sorter) ? sorter[0] : sorter;
              const field = (sort?.field ?? sort?.columnKey) as string | undefined;
              if (!sort?.order) {
                setInsurerSortField('wide_area_union_code');
                setInsurerSortDir('ascend');
                return;
              }
              if (field) {
                setInsurerSortField(field as InsurerSortField);
                setInsurerSortDir(sort.order as SortDir);
              }
            }}
          />
        </div>
        <p className="mt-2 px-1 text-sm text-gray-600">{wideAreaInfoText}</p>
      </div>

      {/* 5. AM/PM */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[400px] border-collapse text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="w-12 p-2"></th>
              <th className="p-2 text-left font-medium">開始</th>
              <th className="p-2 text-left font-medium">終了</th>
              {WEEKDAY_LABELS_WITH_KEY.map(({ label }) => (
                <th key={label} className="p-2 text-center font-medium">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <th className="p-2 text-left text-xs font-medium text-gray-700">午前</th>
              <td className="p-2">
                <Controller
                  name={`${prefix}.am_time_start` as const}
                  control={control}
                  render={({ field }) => {
                    const parts = timeToParts(field.value);
                    return (
                      <div className="flex items-center gap-1">
                        <AppSelect
                          className="min-w-[56px]"
                          options={amHourOptions}
                          value={parts != null ? parts.hour : ''}
                          onChange={v => {
                            const h = v === '' ? null : Number(v);
                            const m = parts?.minute ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                        <span className="text-gray-500">:</span>
                        <AppSelect
                          className="min-w-[56px]"
                          options={MINUTE_OPTIONS}
                          value={parts != null ? parts.minute : ''}
                          onChange={v => {
                            const m = v === '' ? null : Number(v);
                            const h = parts?.hour ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                      </div>
                    );
                  }}
                />
              </td>
              <td className="p-2">
                <Controller
                  name={`${prefix}.am_time_end` as const}
                  control={control}
                  render={({ field }) => {
                    const parts = timeToParts(field.value);
                    return (
                      <div className="flex items-center gap-1">
                        <AppSelect
                          className="min-w-[56px]"
                          options={amHourOptions}
                          value={parts != null ? parts.hour : ''}
                          onChange={v => {
                            const h = v === '' ? null : Number(v);
                            const m = parts?.minute ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                        <span className="text-gray-500">:</span>
                        <AppSelect
                          className="min-w-[56px]"
                          options={MINUTE_OPTIONS}
                          value={parts != null ? parts.minute : ''}
                          onChange={v => {
                            const m = v === '' ? null : Number(v);
                            const h = parts?.hour ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                      </div>
                    );
                  }}
                />
              </td>
              {WEEKDAY_LABELS_WITH_KEY.map(({ key, label }) => (
                <td key={key} className="p-2 text-center">
                  <Controller
                    name={`${prefix}.is_am_${key}` as const}
                    control={control}
                    render={({ field }) => (
                      <AppCheckbox
                        checked={!!field.value}
                        onChange={e => field.onChange(e.target.checked)}
                        aria-label={label}
                        disabled={disabled}
                      />
                    )}
                  />
                </td>
              ))}
            </tr>
            <tr className="border-b">
              <th className="p-2 text-left text-xs font-medium text-gray-700">午後</th>
              <td className="p-2">
                <Controller
                  name={`${prefix}.pm_time_start` as const}
                  control={control}
                  render={({ field }) => {
                    const parts = timeToParts(field.value);
                    return (
                      <div className="flex items-center gap-1">
                        <AppSelect
                          className="min-w-[56px]"
                          options={pmHourOptions}
                          value={parts != null ? parts.hour : ''}
                          onChange={v => {
                            const h = v === '' ? null : Number(v);
                            const m = parts?.minute ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                        <span className="text-gray-500">:</span>
                        <AppSelect
                          className="min-w-[56px]"
                          options={MINUTE_OPTIONS}
                          value={parts != null ? parts.minute : ''}
                          onChange={v => {
                            const m = v === '' ? null : Number(v);
                            const h = parts?.hour ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                      </div>
                    );
                  }}
                />
              </td>
              <td className="p-2">
                <Controller
                  name={`${prefix}.pm_time_end` as const}
                  control={control}
                  render={({ field }) => {
                    const parts = timeToParts(field.value);
                    return (
                      <div className="flex items-center gap-1">
                        <AppSelect
                          className="min-w-[56px]"
                          options={pmHourOptions}
                          value={parts != null ? parts.hour : ''}
                          onChange={v => {
                            const h = v === '' ? null : Number(v);
                            const m = parts?.minute ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                        <span className="text-gray-500">:</span>
                        <AppSelect
                          className="min-w-[56px]"
                          options={MINUTE_OPTIONS}
                          value={parts != null ? parts.minute : ''}
                          onChange={v => {
                            const m = v === '' ? null : Number(v);
                            const h = parts?.hour ?? 0;
                            field.onChange(partsToTime(h, m, { withSeconds: true }));
                          }}
                          disabled={disabled}
                        />
                      </div>
                    );
                  }}
                />
              </td>
              {WEEKDAY_LABELS_WITH_KEY.map(({ key, label }) => (
                <td key={key} className="p-2 text-center">
                  <Controller
                    name={`${prefix}.is_pm_${key}` as const}
                    control={control}
                    render={({ field }) => (
                      <AppCheckbox
                        checked={!!field.value}
                        onChange={e => field.onChange(e.target.checked)}
                        aria-label={label}
                        disabled={disabled}
                      />
                    )}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </AppCard>
  );
}
